// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useState } from "react";
import { z } from "zod";
import { AuthLinkButton } from "@app/components/atoms/auth-link-button";
import { AuthHeading } from "@app/components/molecules/auth-heading";
import { mapAuthError } from "@app/lib/auth-errors";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import {
  Button,
  ValidatedForm,
  ValidatedInput,
  createFormSchema,
  useValidatedForm,
} from "@app/lib/ui-foundation";

const oneTimeCodeSchema = createFormSchema({
  code: z
    .string()
    .trim()
    .min(6, "Enter the full one-time code.")
    .max(64, "Code is too long."),
});

type OneTimeCodeValues = z.infer<typeof oneTimeCodeSchema>;

export type OtcFormProps = {
  onSuccess: () => void;
  onSwitchToLogin?: () => void;
  headingAs?: "h1" | "h2";
};

export function OtcForm({
  onSuccess,
  onSwitchToLogin,
  headingAs = "h2",
}: OtcFormProps) {
  const { exchangeCode } = useJwtAuth();
  const [errorMessage, setErrorMessage] = useState("");

  const oneTimeCodeForm = useValidatedForm(oneTimeCodeSchema, {
    mode: "onChange",
    defaultValues: { code: "" },
  });

  const handleSubmit = useCallback(
    async (values: OneTimeCodeValues) => {
      setErrorMessage("");
      try {
        await exchangeCode(values.code);
        onSuccess();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[otc] exchange failed", error);
        }
        setErrorMessage(
          mapAuthError(
            error,
            "That code didn't work. Request a new one and try again.",
          ),
        );
      }
    },
    [exchangeCode, onSuccess],
  );

  return (
    <>
      <AuthHeading
        as={headingAs}
        title="Exchange one-time code"
        subtitle="Paste the one-time code to continue your session."
      />

      <ValidatedForm
        form={oneTimeCodeForm}
        onSubmit={handleSubmit}
        className="events-form-stack"
      >
        <ValidatedInput
          form={oneTimeCodeForm}
          name="code"
          label="One-time code"
          placeholder="Paste code"
          autoComplete="one-time-code"
        />

        <p className="events-auth-card__error" role="alert" aria-live="polite">
          {errorMessage}
        </p>

        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          type="submit"
          isDisabled={!oneTimeCodeForm.isValid || oneTimeCodeForm.isSubmitting}
        >
          Continue
        </Button>
      </ValidatedForm>

      {onSwitchToLogin ? (
        <div className="events-auth-card__footer">
          <AuthLinkButton onClick={onSwitchToLogin}>
            Back to login
          </AuthLinkButton>
        </div>
      ) : null}
    </>
  );
}
